const ALLOWED_TAGS = new Set([
	'a',
	'abbr',
	'b',
	'blockquote',
	'br',
	'caption',
	'code',
	'div',
	'em',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'hr',
	'i',
	'img',
	'li',
	'ol',
	'p',
	'pre',
	's',
	'small',
	'span',
	'strong',
	'sub',
	'sup',
	'table',
	'tbody',
	'td',
	'th',
	'thead',
	'tr',
	'u',
	'ul',
]);

const GLOBAL_ALLOWED_ATTRIBUTES = new Set([
	'align',
	'aria-hidden',
	'aria-label',
	'class',
	'colspan',
	'id',
	'role',
	'rowspan',
	'style',
	'title',
	'valign',
	'width',
	'height',
]);

const ALLOWED_ATTRIBUTES_BY_TAG: Record<string, Set<string>> = {
	a: new Set(['href', 'target', 'rel', 'name']),
	img: new Set(['src', 'alt']),
};

const URL_ATTRIBUTES = new Set(['href', 'src']);
const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const BLANK_TARGET = '_blank';
const REL_NOOPENER = 'noopener';
const REL_NOREFERRER = 'noreferrer';

const escapeHtml = (value: string) =>
	value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');

const getBaseUrl = () => {
	if (typeof window !== 'undefined' && window.location?.origin) {
		return window.location.origin;
	}

	return 'https://sanitizer.local';
};

const sanitizeStyleValue = (value: string) =>
	value
		.replace(/expression\s*\(/gi, '')
		.replace(/@import/gi, '')
		.replace(/url\s*\(\s*['"]?\s*javascript:/gi, 'url(');

const sanitizeUrl = (value: string): string | null => {
	const trimmed = value.trim();
	if (!trimmed) return '';

	if (trimmed.startsWith('#')) return trimmed;

	try {
		const parsed = new URL(trimmed, getBaseUrl());
		if (!SAFE_PROTOCOLS.has(parsed.protocol)) return null;

		if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)) {
			return parsed.toString();
		}

		return trimmed;
	} catch {
		return null;
	}
};

const addRelProtection = (element: Element) => {
	if (element.getAttribute('target') !== BLANK_TARGET) return;

	const existingRel = (element.getAttribute('rel') ?? '')
		.split(/\s+/)
		.map((token) => token.trim().toLowerCase())
		.filter(Boolean);

	if (!existingRel.includes(REL_NOOPENER)) existingRel.push(REL_NOOPENER);
	if (!existingRel.includes(REL_NOREFERRER)) existingRel.push(REL_NOREFERRER);
	element.setAttribute('rel', existingRel.join(' '));
};

const unwrapElement = (element: Element) => {
	const parent = element.parentNode;
	if (!parent) return;

	while (element.firstChild) {
		parent.insertBefore(element.firstChild, element);
	}

	parent.removeChild(element);
};

export const sanitizeHtml = (html: string): string => {
	if (!html) return '';
	if (typeof DOMParser === 'undefined') return escapeHtml(html);

	const doc = new DOMParser().parseFromString(html, 'text/html');
	const elements = Array.from(doc.body.querySelectorAll('*'));

	for (const element of elements) {
		const tag = element.tagName.toLowerCase();
		if (!ALLOWED_TAGS.has(tag)) {
			unwrapElement(element);
			continue;
		}

		const tagAttributes = ALLOWED_ATTRIBUTES_BY_TAG[tag] ?? new Set<string>();
		const attributes = Array.from(element.attributes);
		for (const attribute of attributes) {
			const name = attribute.name.toLowerCase();
			const value = attribute.value;
			const isAllowedGlobal = GLOBAL_ALLOWED_ATTRIBUTES.has(name);
			const isAllowedTagSpecific = tagAttributes.has(name);
			const isDataOrAria = name.startsWith('data-') || name.startsWith('aria-');

			if (name.startsWith('on') || (!isAllowedGlobal && !isAllowedTagSpecific && !isDataOrAria)) {
				element.removeAttribute(attribute.name);
				continue;
			}

			if (name === 'style') {
				const sanitizedStyle = sanitizeStyleValue(value);
				if (sanitizedStyle) {
					element.setAttribute(attribute.name, sanitizedStyle);
				} else {
					element.removeAttribute(attribute.name);
				}
				continue;
			}

			if (URL_ATTRIBUTES.has(name)) {
				const sanitizedUrl = sanitizeUrl(value);
				if (sanitizedUrl === null) {
					element.removeAttribute(attribute.name);
				} else {
					element.setAttribute(attribute.name, sanitizedUrl);
				}
			}
		}

		addRelProtection(element);
	}

	return doc.body.innerHTML;
};
